<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from '@/components/validations'
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Customers from "@/services/Customers";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
    components: {
        validationMessages, vueDropzone: vue2Dropzone, flatPickr
    },
    props: {
        packData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            csrf_token: localStorage.getItem('csrf_token'),
            name: '',
            currency: 'EUR',
            nft_pack_type: 'match',
            start_time: '',
            active: 1,
            nft_pack_images: [
                { image_url: '', rarity: 0, label: 'Common', file: null, previewUrl: null },
                { image_url: '', rarity: 1, label: 'Silver', file: null, previewUrl: null },
                { image_url: '', rarity: 2, label: 'Gold', file: null, previewUrl: null },
            ],
            submitted: false,
            showModal: false,
            tryingToEdit: false,
            loadImages: false,
            datetimeconfig: {
                enableTime: true,
                time_24hr: true,
                minuteIncrement: 1,
                dateFormat: "Y-m-d H:i:S",
            },
            dropzoneOptions_primary: {
                url: '/',
                autoProcessQueue: false,
                addRemoveLinks: true,
                thumbnailWidth: 600,
                thumbnailHeight: 600,
                thumbnailMethod: 'contain',
                maxFilesize: 4,
                acceptedFiles: 'image/jpeg,image/png,image/bmp,image/webp',
                maxFiles: 1,
            },
            currencyOptions: [
                { value: 'EUR', text: 'EUR' },
            ],
            nftPackTypes: [
                { value: 'match', text: 'Match' },
            ]
        };
    },
    validations: {
        name: { required },
        currency: { required },
        nft_pack_type: { required },
        start_time: { required },
        active: { required },
    },
    watch: {
        showModal(val) {
            if (val) this.populateFromProps();
        }
    },
    computed: {
        matchStarted() {
            const p = this.packData;
            if (!p) return false;
            let ts;
            if (p.start_time_unix) {
                ts = Number(p.start_time_unix) * 1000;
            } else if (p.start_time) {
                ts = new Date(p.start_time).getTime();
            } else {
                return false;
            }
            if (isNaN(ts)) return false;
            return ts <= Date.now();
        },
        hasNewImages() {
            return this.nft_pack_images.some(img => img.file);
        },
        allImagesUploaded() {
            return this.nft_pack_images.every(img => img.image_url || img.file);
        }
    },
    methods: {
        populateFromProps() {
            const p = this.packData;
            this.name = p.name || '';
            this.currency = p.currency || 'EUR';
            this.nft_pack_type = p.nft_pack_type || p.type || 'match';
            this.active = p.active !== undefined ? p.active : 1;

            if (p.start_time) {
                const ts = Number(p.start_time);
                this.start_time = (!isNaN(ts) && ts > 1000000000)
                    ? new Date(ts * 1000).toISOString().slice(0, 19).replace('T', ' ')
                    : p.start_time;
            }

            this.nft_pack_images = this.defaultRarities();
            if (p.id) this.getNFTPackImages();
        },

        defaultRarities() {
            return [
                { rarity: 0, label: 'Common', image_url: '', previewUrl: null, file: null },
                { rarity: 1, label: 'Silver', image_url: '', previewUrl: null, file: null },
                { rarity: 2, label: 'Gold', image_url: '', previewUrl: null, file: null },
            ];
        },

        async getNFTPackImages() {
            this.loadImages = true;
            try {
                const response = await Customers.getNFTPackImages(this.packData.id);
                const images = response.data?.data || [];
                const bust = Date.now();
                this.nft_pack_images = this.defaultRarities().map(r => {
                    const existing = images.find(img => Number(img.rarity) === r.rarity);
                    const url = existing?.image_url || '';
                    return {
                        ...r,
                        image_url: url,
                        previewUrl: url ? `${url}${url.includes('?') ? '&' : '?'}t=${bust}` : null,
                    };
                });
            } catch (error) {
                const errorMsg = error.response?.data?.error || "Failed to load pack images";
                this.failedmsg(errorMsg);
            } finally {
                this.loadImages = false;
            }
        },

        selectPackImage(files, rarity) {
            if (files.length > 1) {
                const ref = `dropzone_rarity_${rarity}`;
                const dz = Array.isArray(this.$refs[ref]) ? this.$refs[ref][0] : this.$refs[ref];
                if (dz) files.slice(1).forEach(f => dz.removeFile(f));
            }
            const file = files[0] || null;
            const idx = this.nft_pack_images.findIndex(i => i.rarity === rarity);
            if (idx !== -1) {
                if (this.nft_pack_images[idx].previewUrl?.startsWith('blob:')) {
                    URL.revokeObjectURL(this.nft_pack_images[idx].previewUrl);
                }
                this.$set(this.nft_pack_images, idx, {
                    ...this.nft_pack_images[idx],
                    file,
                    previewUrl: file ? URL.createObjectURL(file) : null,
                });
            }
        },

        clearPackImage(rarity) {
            const idx = this.nft_pack_images.findIndex(i => i.rarity === rarity);
            if (idx === -1) return;
            const img = this.nft_pack_images[idx];
            if (img.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(img.previewUrl);
            this.$set(this.nft_pack_images, idx, { ...img, file: null, previewUrl: null, image_url: '' });
            const ref = `dropzone_rarity_${rarity}`;
            const dz = Array.isArray(this.$refs[ref]) ? this.$refs[ref][0] : this.$refs[ref];
            if (dz) dz.removeAllFiles();
        },

        async editNFTPack() {
            this.tryingToEdit = true;
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.tryingToEdit = false;
                return;
            }

            try {
                const p = this.packData;
                const name = this.matchStarted ? (p.name || '') : this.name;
                const currency = this.matchStarted ? (p.currency || 'EUR') : this.currency;
                const type = this.matchStarted ? (p.type || p.nft_pack_type || 'match') : this.nft_pack_type;
                const active = this.matchStarted ? (p.active !== undefined ? p.active : 1) : this.active;
                const startTime = this.matchStarted
                    ? (p.start_time_unix || Math.floor(new Date(p.start_time).getTime() / 1000))
                    : Math.floor(new Date(this.start_time).getTime() / 1000);

                const formData = new FormData();
                formData.append('name', name);
                formData.append('currency', currency);
                formData.append('type', type);
                formData.append('start_time', String(startTime));
                formData.append('active', active);
                formData.append('csrf_token', this.csrf_token);

                const changedImages = this.nft_pack_images.filter(img => img.file);
                changedImages.forEach((img, index) => {
                    formData.append(`nftPackImages[${index}][pack_id]`, this.packData.id);
                    formData.append(`nftPackImages[${index}][rarity]`, img.rarity);
                    formData.append(`nftPackImages[${index}][image_url]`, img.file);
                });
                await Customers.editNFTPack(this.packData.id, formData);
                this.successmsg("NFT Pack updated");
                this.closeModal();
            } catch (error) {
                const errorMsg = error.response?.data?.error || "Failed to update NFT Pack";
                this.failedmsg(errorMsg);
            } finally {
                this.refreshData();
                this.tryingToEdit = false;
            }
        },

        refreshData() {
            this.$emit('onRefresh');
        },
        closeModal() {
            this.showModal = false;
            this.resetProps();
        },
        resetProps() {
            this.nft_pack_images.forEach(img => {
                if (img.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(img.previewUrl);
            });
            this.name = "";
            this.currency = "EUR";
            this.nft_pack_type = "match";
            this.start_time = "";
            this.active = 1;
            this.nft_pack_images = this.defaultRarities();
            this.submitted = false;
            this.loadImages = false;
        }
    },
};
</script>

<template>
    <b-modal v-model="showModal" size="xl" @hidden="resetProps" id="edit_nft_pack" hide-header hide-footer
        body-class="p-0" centered>
        <div class="add-nft-pack-modal">

            <header class="add-nft-pack-modal__hero">
                <h2>Edit NFT Pack</h2>
                <p>Update the details of this NFT Pack</p>
            </header>

            <b-form @submit.prevent="editNFTPack">
                <!-- PACK DETAILS -->
                <section class="nft-pack-card">
                    <header class="nft-pack-card__header">
                        <h3>Pack Details</h3>
                        <p>Update the details for this pack</p>
                    </header>

                    <b-alert v-if="matchStarted && allImagesUploaded" show variant="danger" class="mb-4">
                        <i class="uil uil-lock me-1"></i>
                        This pack is fully locked — the match has started and all images are already uploaded.
                    </b-alert>
                    <b-alert v-else-if="matchStarted" show variant="warning" class="mb-4">
                        <i class="uil uil-info-circle me-1"></i>
                        This match has already started — pack details are locked. You can still upload the missing images.
                    </b-alert>

                    <b-form-group label="Pack Name *" class="mb-4">
                        <b-form-input v-model.trim="name"
                            :disabled="matchStarted"
                            :state="submitted && $v.name.$error ? false : null" />
                        <validationMessages v-if="submitted" :fieldName="'Name'" :validationName="$v.name" />
                    </b-form-group>

                    <b-form-group label="Start Time *" class="mb-4">
                        <flat-pickr v-model="start_time" :config="datetimeconfig" class="form-control"
                            :disabled="matchStarted"
                            :class="{ 'is-invalid': submitted && $v.start_time.$error }" />
                        <validationMessages v-if="submitted" :fieldName="'Start Time'" :validationName="$v.start_time" />
                    </b-form-group>

                    <b-form-group label="Currency *" class="mb-4">
                        <b-form-select v-model="currency" :options="currencyOptions"
                            :disabled="matchStarted"
                            :class="{ 'is-invalid': submitted && $v.currency.$error }" />
                        <validationMessages v-if="submitted" :fieldName="'NFT Pack Currency'" :validationName="$v.currency" />
                    </b-form-group>

                    <b-form-group label="Type *" class="mb-4">
                        <b-form-select v-model="nft_pack_type" :options="nftPackTypes"
                            :disabled="matchStarted"
                            :class="{ 'is-invalid': submitted && $v.nft_pack_type.$error }" />
                        <validationMessages v-if="submitted" :fieldName="'NFT Pack Type'" :validationName="$v.nft_pack_type" />
                    </b-form-group>

                    <b-form-group label="Active" class="mb-0">
                        <div class="form-check form-switch form-switch-lg mb-0">
                            <input v-model="active" type="checkbox" class="form-check-input"
                                :disabled="matchStarted"
                                :true-value="1" :false-value="0" />
                        </div>
                    </b-form-group>
                </section>

                <!-- IMAGES -->
                <section class="nft-pack-card">
                    <header class="nft-pack-card__header">
                        <h3>Pack Images</h3>
                        <p>Existing images are locked. You can only upload images for rarities that are missing.</p>
                    </header>

                    <div v-if="loadImages" class="d-flex align-items-center justify-content-center py-5">
                        <b-spinner />
                    </div>

                    <div v-else class="row g-3">
                        <div v-for="img in nft_pack_images" :key="img.rarity" class="col-12 col-md-4">
                            <label class="d-block mb-2 fw-semibold">{{ img.label }}</label>
                            <div class="poll-upload poll-upload--compact">
                                <div v-if="img.previewUrl" class="pack-image-preview">
                                    <img :src="img.previewUrl" :alt="img.label" class="pack-preview-img" />
                                    <div class="pack-preview-actions">
                                        <span v-if="img.file" class="pack-preview-name">{{ img.file.name }}</span>
                                        <span v-else-if="matchStarted" class="pack-preview-name text-muted">
                                            <i class="uil uil-lock me-1"></i>Locked
                                        </span>
                                        <span v-else class="pack-preview-name text-muted">Current image</span>
                                        <a v-if="img.file || !matchStarted" href="#" class="pack-remove-link"
                                            @click.prevent="clearPackImage(img.rarity)">
                                            <i class="uil uil-trash-alt me-1"></i>Remove
                                        </a>
                                    </div>
                                </div>
                                <vue-dropzone v-show="!img.previewUrl"
                                    :id="`dropzone_edit_rarity_${img.rarity}`"
                                    :ref="`dropzone_rarity_${img.rarity}`"
                                    :use-custom-slot="true"
                                    :options="dropzoneOptions_primary"
                                    @vdropzone-files-added="files => selectPackImage(files, img.rarity)"
                                    no-progress-bar>
                                    <div class="poll-upload__placeholder">
                                        <i class="uil uil-import"></i>
                                        <span>Click or drop</span>
                                        <small>PNG, JPG up to 4MB</small>
                                    </div>
                                </vue-dropzone>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- FOOTER -->
                <footer class="add-nft-pack-modal__footer">
                    <b-button variant="outline-secondary" @click="closeModal">Cancel</b-button>
                    <b-button variant="dark" type="submit"
                        :disabled="tryingToEdit || loadImages || (matchStarted && !hasNewImages)">
                        <b-spinner v-if="tryingToEdit" small class="me-2" />
                        Save Changes
                    </b-button>
                </footer>
            </b-form>
        </div>
    </b-modal>
</template>

<style scoped>
.add-nft-pack-modal {
    padding: 32px;
    background: #f7f8fb;
}

.add-nft-pack-modal__hero {
    margin-bottom: 24px;
}

.add-nft-pack-modal__hero h2 {
    font-weight: 600;
    font-size: 28px;
    margin-bottom: 8px;
    color: #151a2d;
}

.add-nft-pack-modal__hero p {
    margin: 0;
    color: #5f6b7d;
    font-size: 16px;
}

.nft-pack-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 28px;
    margin-bottom: 24px;
    box-shadow: 0 12px 32px rgb(15 23 42 / 6%);
}

.nft-pack-card__header h3 {
    margin-bottom: 4px;
    font-size: 20px;
    font-weight: 600;
    color: #151a2d;
}

.nft-pack-card__header p {
    margin-bottom: 24px;
    color: #5f6b7d;
}

.poll-upload {
    border: 2px dashed #d3d8e6;
    border-radius: 16px;
    background: #f7f8fb;
    padding: 32px;
    transition: border-color 0.2s ease, background 0.2s ease;
}

.poll-upload--compact {
    padding: 14px;
}

.poll-upload--compact .poll-upload__placeholder {
    font-size: 12px;
}

.poll-upload--compact .poll-upload__placeholder i {
    font-size: 22px;
}

.poll-upload--compact ::v-deep .dropzone {
    min-height: 340px;
    padding: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.poll-upload--compact ::v-deep .dz-message {
    margin: 0;
    width: 100%;
    text-align: center;
}

.poll-upload--compact ::v-deep .dz-preview {
    margin: 0;
    width: 100%;
    min-height: 340px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.poll-upload--compact ::v-deep .dz-preview .dz-image {
    width: 100% !important;
    height: 300px !important;
    border-radius: 8px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.poll-upload--compact ::v-deep .dz-preview .dz-image img {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    object-position: center !important;
    top: auto !important;
    left: auto !important;
    position: static !important;
    transform: none !important;
}

.poll-upload--compact ::v-deep .dz-preview .dz-details {
    display: none;
}

.poll-upload.has-error {
    border-color: #f97373;
    background: #fff5f5;
}

.poll-upload__placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #5f6b7d;
    font-size: 14px;
}

.poll-upload__placeholder i {
    font-size: 32px;
    color: #151a2d;
}

.poll-upload__placeholder small {
    color: #a0aec0;
}

/* pack image preview */
.pack-image-preview {
    width: 100%;
}

.pack-preview-img {
    width: 100%;
    height: 300px;
    object-fit: contain;
    border-radius: 8px;
    background: #fff;
    display: block;
}

.pack-preview-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 2px 0;
}

.pack-preview-name {
    font-size: 12px;
    color: #5f6b7d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 75%;
}

.pack-remove-link {
    font-size: 12px;
    color: #f97373;
    text-decoration: none;
    white-space: nowrap;
}

.pack-remove-link:hover {
    color: #e05555;
}

.add-nft-pack-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 12px;
}

@media (max-width: 767.98px) {
    .add-nft-pack-modal {
        padding: 20px;
    }

    .nft-pack-card {
        padding: 20px;
    }
}
</style>
